import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Star, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

export default function ReviewForm() {
  const { token } = useParams();
  const { lang } = useLang();
  const [ctx, setCtx] = useState(undefined);
  const [brand, setBrand] = useState({});
  const [rating, setRating] = useState(0);
  const [tripText, setTripText] = useState("");
  const [petStory, setPetStory] = useState("");
  const [display, setDisplay] = useState("");
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/review/context/${token}`).then((r) => { setCtx(r.data); if (r.data.already_submitted) setDone(true); }).catch(() => setCtx(null));
    api.get("/brand").then((r) => setBrand(r.data)).catch(() => {});
  }, [token]);

  const es = (ctx?.language || lang) === "es";
  const submit = async () => {
    if (!rating) return toast.error(es ? "Elija una calificación" : "Please choose a star rating");
    if (!agree) return toast.error(es ? "Confirme el acuerdo de la política" : "Please agree to the Review Policy");
    setBusy(true);
    try {
      await api.post(`/review/${token}`, { rating, trip_text: tripText, pet_story: petStory, display_name: display, agree_policy: agree });
      setDone(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit"); }
    finally { setBusy(false); }
  };

  if (ctx === undefined) return null;
  if (ctx === null) return <div className="mx-auto max-w-lg px-6 py-20 text-center text-charcoal-600" data-testid="review-invalid">{es ? "Este enlace de reseña no es válido o expiró." : "This review link is not valid or has expired."}</div>;

  if (done) {
    return (
      <div className="mx-auto max-w-lg px-6 py-20 text-center" data-testid="review-thankyou">
        <CheckCircle2 size={48} className="text-sage-600 mx-auto mb-4" />
        <h1 className="text-2xl font-normal text-charcoal-800 mb-2">{es ? "¡Gracias!" : "Thank you!"}</h1>
        <p className="text-charcoal-600 mb-6">{es ? "Agradecemos que comparta su experiencia." : "We appreciate you sharing your experience."}</p>
        {brand.google_review_link && (
          <a href={brand.google_review_link} target="_blank" rel="noreferrer" data-testid="review-google-btn" className="inline-block rounded-full bg-terracotta text-white px-6 py-3 text-sm">{es ? "Compartir también en Google" : "Share on Google too"}</a>
        )}
        <div className="mt-6"><Link to="/" className="text-charcoal-500 underline text-sm">{es ? "Volver al inicio" : "Back to home"}</Link></div>
      </div>
    );
  }

  const pet = ctx.pet_name;
  return (
    <div className="mx-auto max-w-lg px-6 py-12" data-testid="review-form">
      <h1 className="text-2xl font-normal text-charcoal-800 mb-6">{es ? "Reseñe su viaje" : "Review your trip"}</h1>
      <div className="flex gap-1 mb-6" data-testid="review-stars">
        {[1, 2, 3, 4, 5].map((i) => (
          <button key={i} onClick={() => setRating(i)} data-testid={`star-${i}`}><Star size={32} className="text-terracotta" fill={i <= rating ? "currentColor" : "none"} strokeWidth={1.5} /></button>
        ))}
      </div>
      <label className="block mb-4">
        <span className="text-sm text-charcoal-600">{es ? "¿Cómo estuvo su viaje?" : "How was your trip?"}</span>
        <textarea data-testid="review-text" rows={6} maxLength={5000} className="mt-1 w-full border rounded-xl px-3 py-2" value={tripText} onChange={(e) => setTripText(e.target.value)} />
        <span className="block text-right text-xs text-charcoal-400 mt-1" data-testid="review-text-count">{(tripText || "").length}/5000</span>
      </label>
      {pet && (
        <label className="block mb-4">
          <span className="text-sm text-charcoal-600">{es ? `¿Cómo le fue a ${pet}? (desde su punto de vista, opcional)` : `How did ${pet} do? (tell it from ${pet}'s point of view, optional)`}</span>
          <textarea data-testid="review-petstory" rows={4} maxLength={1500} className="mt-1 w-full border rounded-xl px-3 py-2" value={petStory} onChange={(e) => setPetStory(e.target.value)} />
          <span className="block text-right text-xs text-charcoal-400 mt-1" data-testid="review-petstory-count">{(petStory || "").length}/1500</span>
        </label>
      )}
      <label className="block mb-4">
        <span className="text-sm text-charcoal-600">{es ? "Nombre para mostrar" : "Display name"}</span>
        <input data-testid="review-display" className="mt-1 w-full border rounded-xl px-3 py-2" value={display} onChange={(e) => setDisplay(e.target.value)} placeholder={es ? "p. ej. María G." : "e.g. Maria G."} />
      </label>
      <label className="flex items-start gap-2 mb-6 text-sm text-charcoal-600">
        <input type="checkbox" data-testid="review-agree" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-1" />
        <span>{es ? "Esta reseña es mi propia experiencia honesta con Hola Movers. Acepto la " : "This review is my own honest experience with Hola Movers. I agree to the "}<Link to="/review-policy" className="text-terracotta underline">{es ? "Política de Reseñas" : "Review Policy"}</Link>.</span>
      </label>
      <button onClick={submit} disabled={busy} data-testid="review-submit" className="w-full rounded-full bg-terracotta text-white py-3 text-sm flex items-center justify-center gap-2 disabled:opacity-60">
        {busy ? <Loader2 size={16} className="animate-spin" /> : null}{es ? "Enviar reseña" : "Submit review"}
      </button>
    </div>
  );
}
